/**
 * OrganisationSetupDialog — the short finish-up after a new assembly is
 * created: who leads it, when it meets, how to reach it, and its public page.
 * Skippable and resumable; the tree row keeps a progress chip until it's done.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Check, Loader2, ExternalLink, UserRound, Clock, Phone, MapPin, Globe2 } from 'lucide-react';

const STEP_META = [
  { key: 'leader', label: 'Who leads it', icon: UserRound },
  { key: 'services', label: 'When it meets', icon: Clock },
  { key: 'contact', label: 'How to reach it', icon: Phone },
  { key: 'location', label: 'Where it is', icon: MapPin },
  { key: 'public_page', label: 'Public page', icon: Globe2 },
];

const done = (form, org) => ({
  leader: !!(form.leader_name || '').trim(),
  services: !!(form.service_times || '').trim(),
  contact: !!((form.contact_email || '').trim() || (form.contact_phone || '').trim()),
  location: !!((form.address || '').trim() || (form.city || '').trim()),
  public_page: !!org?.setup?.steps?.public_page,
});

export const OrganisationSetupDialog = ({ open, onOpenChange, org }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({});

  // Opening the dialog for a different organisation must start from that
  // organisation's own values, never the last one's.
  useEffect(() => {
    setForm({
      leader_name: org?.leader_name || '', leader_title: org?.leader_title || '',
      service_times: org?.service_times || '', contact_email: org?.contact_email || '',
      contact_phone: org?.contact_phone || '', address: org?.address || '',
      city: org?.city || '',
    });
  }, [org?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => ['org-tree', 'org-flat', 'admin-churches', 'org-leaders']
    .forEach((k) => queryClient.invalidateQueries({ queryKey: [k] }));

  const save = useMutation({
    mutationFn: async (payload) => (await api.put(`/admin/organisations/${org.id}`, payload)).data,
    onSuccess: () => { toast.success(`${org.name} updated`); refresh(); onOpenChange(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const later = useMutation({
    mutationFn: async () => (await api.put(`/admin/organisations/${org.id}`,
      { setup_dismissed: true })).data,
    onSuccess: () => { refresh(); onOpenChange(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!org) return null;
  const state = done(form, org);
  const count = Object.values(state).filter(Boolean).length;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="organisation-setup-dialog">
        <DialogHeader>
          <DialogTitle>Finish setting up {org.name}</DialogTitle>
          <DialogDescription>
            Four quick fields and the public page — everything here can wait if you're in a hurry.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2" data-testid="setup-progress">
          {STEP_META.map((s) => (
            <span key={s.key} data-testid={`setup-chip-${s.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
                    state[s.key] ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
              {state[s.key] ? <Check className="h-3 w-3" /> : <s.icon className="h-3 w-3" />}
              {s.label}
            </span>
          ))}
          <span className="text-[11px] text-slate-400 self-center ml-1" data-testid="setup-count">
            {count} of 5 done
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="su-leader">Leader</Label>
            <Input id="su-leader" data-testid="setup-leader-name" value={form.leader_name}
                   onChange={set('leader_name')} placeholder="Full name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-title">Leader title</Label>
            <Input id="su-title" data-testid="setup-leader-title" value={form.leader_title}
                   onChange={set('leader_title')} placeholder="Resident Pastor" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="su-services">Service times</Label>
            <Input id="su-services" data-testid="setup-service-times" value={form.service_times}
                   onChange={set('service_times')} placeholder="Sun 9am & 11am · Wed 6pm" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-email">Contact email</Label>
            <Input id="su-email" data-testid="setup-contact-email" value={form.contact_email}
                   onChange={set('contact_email')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-phone">Contact phone</Label>
            <Input id="su-phone" data-testid="setup-contact-phone" value={form.contact_phone}
                   onChange={set('contact_phone')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-address">Address</Label>
            <Input id="su-address" data-testid="setup-address" value={form.address}
                   onChange={set('address')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="su-city">City</Label>
            <Input id="su-city" data-testid="setup-city" value={form.city} onChange={set('city')} />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 flex items-center gap-3">
          <Globe2 className="h-4 w-4 text-slate-400 shrink-0" />
          <p className="text-xs text-slate-600 flex-1">
            {state.public_page
              ? 'The public page is live.'
              : 'The public page is not published yet — add a welcome, photos and service details.'}
          </p>
          <Button variant="outline" size="sm" data-testid="setup-open-public-page"
                  onClick={() => { onOpenChange(false); navigate(`/admin/churches/${org.id}`); }}>
            Open editor <ExternalLink className="h-3 w-3 ml-1.5" />
          </Button>
        </div>

        <div className="flex justify-between pt-2">
          <Button variant="ghost" size="sm" data-testid="setup-later-btn"
                  disabled={later.isPending} onClick={() => later.mutate()}>
            I&apos;ll do this later
          </Button>
          <Button data-testid="setup-save-btn" disabled={save.isPending}
                  onClick={() => save.mutate(form)}>
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrganisationSetupDialog;
